import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getImageCollections, getImageFiles } from '../api/images';
import { useImageToolsOverlay } from '../context/ImageToolsOverlayContext';
import { CodePopup } from './CodeSnippet';

// Helper component for TabPanel
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <div className="p-2">
          {children}
        </div>
      )}
    </div>
  );
}

const ImageBrowser = () => {
  const { user } = useAuth();
  const { openOverlay } = useImageToolsOverlay();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(20);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchParams, setSearchParams] = useState({
    filter: '',
    objectName: '',
    raMin: '',
    raMax: '',
    decMin: '',
    decMax: '',
    raCentral: '',
    decCentral: '',
    radius: '1.0',
    imageFilter: '',
    obsdateMin: '',
    obsdateMax: ''
  });
  
  const [expandedSections, setExpandedSections] = useState({
    basicSearch: true,
    pointSearch: false,
    regionSearch: false
  });

  const [codePopup, setCodePopup] = useState({ isOpen: false, snippetName: '', dynamicData: {} });

  // Track whether point search or region search is active
  const [activeSearchMethod, setActiveSearchMethod] = useState('basic');
  
  // Fetch collections on mount
  useEffect(() => {
    fetchCollections();
  }, []);
  
  // Fetch images when collection changes
  useEffect(() => {
    if (selectedCollection) {
      fetchImageFiles(selectedCollection.id);
    }
  }, [selectedCollection, page]);
  
  const fetchCollections = async () => {
    setLoading(true);
    try {
      const data = await getImageCollections();
      setCollections(data);
      setError(null);
      
      // Select first collection by default if available
      if (data.length > 0 && !selectedCollection) {
        setSelectedCollection(data[0]);
      }
    } catch (err) {
      console.error('Error fetching collections:', err);
      setError('Failed to fetch image collections you have access to');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchImageFiles = async (collectionId) => {
    setLoading(true);
    try {
      // Prepare options for the API call
      const options = {
        skip: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      // Only include "basic search" params when basic is active
      if (activeSearchMethod === 'basic') {
        options.filter = searchParams.imageFilter;
        options.objectName = searchParams.objectName;

        if (searchParams.filter) options.filter_str = searchParams.filter;

        if (searchParams.obsdateMin) options.obsdate_min = searchParams.obsdateMin;
        if (searchParams.obsdateMax) options.obsdate_max = searchParams.obsdateMax;
      }

      // Add search parameters based on active search method
      if (
        activeSearchMethod === 'point' &&
        searchParams.raCentral &&
        searchParams.decCentral &&
        searchParams.radius
      ) {
        options.ra_center = parseFloat(searchParams.raCentral);
        options.dec_center = parseFloat(searchParams.decCentral);
        options.radius = parseFloat(searchParams.radius);
      } else if (
        activeSearchMethod === 'region' &&
        searchParams.raMin &&
        searchParams.raMax &&
        searchParams.decMin &&
        searchParams.decMax
      ) {
        options.ra_min = parseFloat(searchParams.raMin);
        options.ra_max = parseFloat(searchParams.raMax);
        options.dec_min = parseFloat(searchParams.decMin);
        options.dec_max = parseFloat(searchParams.decMax);
      }
      
      // Add text filter
      if (searchParams.filter) options.filter_str = searchParams.filter;

      // Add search parameters based on active search method
      if (activeSearchMethod === 'point' && searchParams.raCentral && searchParams.decCentral && searchParams.radius) {
        // Add cone search parameters (using ra, dec, radius as expected by the backend)
        options.ra_center = parseFloat(searchParams.raCentral);
        options.dec_center = parseFloat(searchParams.decCentral);
        options.radius = parseFloat(searchParams.radius);
      } 
      else if (activeSearchMethod === 'region' && searchParams.raMin && searchParams.raMax && searchParams.decMin && searchParams.decMax) {
        // Add box search parameters
        options.ra_min = parseFloat(searchParams.raMin);
        options.ra_max = parseFloat(searchParams.raMax);
        options.dec_min = parseFloat(searchParams.decMin);
        options.dec_max = parseFloat(searchParams.decMax);
      }
      
      // Add observation date parameters
      if (searchParams.obsdateMin) {
        options.obsdate_min = searchParams.obsdateMin;
      }
      
      if (searchParams.obsdateMax) {
        options.obsdate_max = searchParams.obsdateMax;
      }
      
      const data = await getImageFiles(collectionId, options);
      
      setImageFiles(data.items || data);
      
      // Better pagination logic
      // Calculate total pages if the response includes count
      if (data.total) {
        setTotalPages(Math.ceil(data.total / itemsPerPage));
      } else {
        // Otherwise estimate based on whether we got a full page
        const dataArray = Array.isArray(data) ? data : (data.items || []);
        setTotalPages(dataArray.length < itemsPerPage ? page : page + 1);
      }
      
    } catch (err) {
      console.error('Error fetching images:', err);
      
      // Provide more descriptive error messages based on search type and error
      if (activeSearchMethod === 'point') {
        setError('Point search failed. The search coordinates or radius might be invalid, or this search type might not be supported by the server.');
      } else if (activeSearchMethod === 'region') {
        setError('Region search failed. The coordinate bounds might be invalid, or this search type might not be supported by the server.');
      } else {
        setError('Failed to fetch images from this collection');
      }
      
      // Clear the image files to prevent displaying stale data
      setImageFiles([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCollectionChange = (collection) => {
    setSelectedCollection(collection);
    setPage(1); // Reset to first page when changing collections
  };
  
  const handleImageClick = (image) => {
    setSelectedImage(image);
    setOpenImageDialog(true);
  };
  
  const handleDownloadImage = async (image) => {
    console.log('Download function called with image:', image);
    
    // Check if image object is valid
    if (!image || !image.id) {
      console.error('Invalid image object:', image);
      setError('Invalid image data for download');
      return;
    }
    
    try {
      // Create a URL for downloading the FITS file
      const downloadUrl = `/adss/v1/images/files/${image.id}/download?token=${localStorage.getItem('token')}`;
      console.log('Attempting download from URL:', downloadUrl);
      
      // Try opening the URL directly in a new tab
      window.open(downloadUrl, '_blank');
      
      console.log('Download initiated');
      
    } catch (err) {
      console.error('Error initiating download:', err);
      setError('Failed to download image: ' + err.message);
    }
  };
  
  // Validate search parameters
  const validateSearch = () => {
    if (activeSearchMethod === 'point') {
      // Check that we have all necessary point search parameters
      if (!searchParams.raCentral || !searchParams.decCentral || !searchParams.radius) {
        setError('Please provide RA center, Dec center and radius for point search');
        return false;
      }
      
      // Validate parameter values
      const ra = parseFloat(searchParams.raCentral);
      const dec = parseFloat(searchParams.decCentral);
      const radius = parseFloat(searchParams.radius);
      
      if (isNaN(ra) || isNaN(dec) || isNaN(radius)) {
        setError('RA, Dec and radius must be valid numbers');
        return false;
      }
      
      if (ra < 0 || ra > 360) {
        setError('RA must be between 0 and 360 degrees');
        return false;
      }
      
      if (dec < -90 || dec > 90) {
        setError('Dec must be between -90 and 90 degrees');
        return false;
      }
      
      if (radius <= 0 || radius > 10) {
        setError('Radius must be between 0 and 10 degrees');
        return false;
      }
    } else if (activeSearchMethod === 'region') {
      // Check that we have all necessary region search parameters
      if (!searchParams.raMin || !searchParams.raMax || !searchParams.decMin || !searchParams.decMax) {
        setError('Please provide minimum and maximum values for RA and Dec');
        return false;
      }
      
      // Validate parameter values
      const raMin = parseFloat(searchParams.raMin);
      const raMax = parseFloat(searchParams.raMax);
      const decMin = parseFloat(searchParams.decMin);
      const decMax = parseFloat(searchParams.decMax);
      
      if (isNaN(raMin) || isNaN(raMax) || isNaN(decMin) || isNaN(decMax)) {
        setError('All coordinate values must be valid numbers');
        return false;
      }
      
      if (raMin < 0 || raMin > 360 || raMax < 0 || raMax > 360) {
        setError('RA values must be between 0 and 360 degrees');
        return false;
      }
      
      if (decMin < -90 || decMin > 90 || decMax < -90 || decMax > 90) {
        setError('Dec values must be between -90 and 90 degrees');
        return false;
      }
      
      if (raMin >= raMax) {
        setError('RA Min must be less than RA Max');
        return false;
      }
      
      if (decMin >= decMax) {
        setError('Dec Min must be less than Dec Max');
        return false;
      }
    }
    
    // Clear any previous validation errors
    setError(null);
    return true;
  };

  const handleSearch = () => {
    if (!selectedCollection) return;
    
    // Skip validation for basic search
    if (activeSearchMethod !== 'basic' && !validateSearch()) {
      return;
    }
      
    // Clear conflicting search parameters based on active search method
    if (activeSearchMethod === 'point') {
      // Clear region search params when using point search
      setSearchParams({
        ...searchParams,
        raMin: '',
        raMax: '',
        decMin: '',
        decMax: ''
      });
    } else if (activeSearchMethod === 'region') {
      // Clear point search params when using region search
      setSearchParams({
        ...searchParams,
        raCentral: '',
        decCentral: '',
        radius: '1.0'
      });
    }
    
    setPage(1); // Reset to first page when searching
    fetchImageFiles(selectedCollection.id);
  };
  
  const handleSearchParamChange = (e) => {
    const { name, value } = e.target;
    
    // Update search parameters
    setSearchParams({
      ...searchParams,
      [name]: value
    });
    
    // If point search parameters are being modified, set point search as active
    if (['raCentral', 'decCentral', 'radius'].includes(name) && value) {
      setActiveSearchMethod('point');
      // Open the point search section if it's not already open
      if (!expandedSections.pointSearch) {
        setExpandedSections({
          ...expandedSections,
          pointSearch: true,
          regionSearch: false
        });
      }
    }
    
    // If region search parameters are being modified, set region search as active
    if (['raMin', 'raMax', 'decMin', 'decMax'].includes(name) && value) {
      setActiveSearchMethod('region');
      // Open the region search section if it's not already open
      if (!expandedSections.regionSearch) {
        setExpandedSections({
          ...expandedSections,
          regionSearch: true,
          pointSearch: false
        });
      }
    }
  };
  
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  const toggleSection = (section) => {
    // If toggling a search section, set it as active and collapse the other
    if (section === 'pointSearch') {
      setExpandedSections({
        ...expandedSections,
        pointSearch: !expandedSections.pointSearch,
        regionSearch: false
      });
      setActiveSearchMethod(expandedSections.pointSearch ? 'basic' : 'point');
    } else if (section === 'regionSearch') {
      setExpandedSections({
        ...expandedSections,
        regionSearch: !expandedSections.regionSearch,
        pointSearch: false
      });
      setActiveSearchMethod(expandedSections.regionSearch ? 'basic' : 'region');
    } else {
      setExpandedSections({
        ...expandedSections,
        [section]: !expandedSections[section]
      });
    }
  };

  const clearFilters = () => {
    setSearchParams({
      filter: '',
      objectName: '',
      raMin: '',
      raMax: '',
      decMin: '',
      decMax: '',
      raCentral: '',
      decCentral: '',
      radius: '1.0',
      imageFilter: '',
      obsdateMin: '',
      obsdateMax: ''
    });
    setActiveSearchMethod('basic');
  };
  
  if (loading && collections.length === 0) {
    return (
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-center items-center min-h-[200px]">
          <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-2">
      {/* Compact Header */}
      <div className="mb-4">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h1 className="text-xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-1">
              Astronomical Images
            </h1>
            <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
              Browse and download astronomical images from available collections
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Legend */}
            <div className="flex items-center gap-3 text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
              <div className="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Download</span>
              </div>
              <div className="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span>Info</span>
              </div>
              <div className="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                <span>Tools</span>
              </div>
              <div className="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Code</span>
              </div>
            </div>

            {selectedCollection && (
              <button
                onClick={() => openOverlay({ collectionId: selectedCollection.id })}
                className="px-3 py-1.5 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                Image Tools
              </button>
            )}
          </div>
        </div>
      </div>
      
      {error && (
        <div className="p-3 mb-3 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md border border-status-error-light-text/20 dark:border-status-error-dark-text/20">
          {error}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Compact Collections sidebar */}
        <div className="md:col-span-1">
          <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
            <div className="p-2 bg-ui-light-hover dark:bg-ui-dark-hover border-b border-ui-light-border dark:border-ui-dark-border">
              <h2 className="text-sm font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">
                Collections ({collections.length})
              </h2>
            </div>
            
            <div className="overflow-auto" style={{ height: 'calc(100% - 60px)' }}>
              {collections.length === 0 ? (
                <div className="p-4 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-ui-light-text-muted dark:text-ui-dark-text-muted mb-2" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                  <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                    No collections available
                  </p>
                </div>
              ) : (
                <div className="p-1">
                  {collections.map((collection) => (
                    <div 
                      key={collection.id} 
                      className={`mb-2 p-2 rounded-md cursor-pointer transition-all ${
                        selectedCollection?.id === collection.id 
                          ? 'bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800/50' 
                          : 'bg-transparent hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'
                      }`}
                      onClick={() => handleCollectionChange(collection)}
                    >
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 mt-0.5 text-primary-600 dark:text-primary-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                        </svg>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary leading-tight">
                            {collection.name}
                          </h3>
                          
                          {collection.description && (
                            <p className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary mt-1 line-clamp-2 leading-tight">
                              {collection.description}
                            </p>
                          )}
                          
                          <p className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted mt-1">
                            {collection.last_scanned 
                              ? new Date(collection.last_scanned).toLocaleDateString() 
                              : 'Never scanned'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Compact Images section */}
        <div className="md:col-span-3">
          {selectedCollection ? (
            <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>
              {/* Compact Header */}
              <div className="p-2 bg-ui-light-hover dark:bg-ui-dark-hover border-b border-ui-light-border dark:border-ui-dark-border flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-sm font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary truncate">
                      {selectedCollection.name}
                    </h2>
                    {selectedCollection.description && (
                      <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary truncate">
                        {selectedCollection.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 ml-2">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                        showFilters 
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400' 
                          : 'bg-ui-light-card dark:bg-ui-dark-card text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'
                      }`}
                    >
                      Filters
                    </button>
                    <button
                      onClick={handleSearch}
                      className="px-2 py-1 text-xs font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Compact Search filters */}
              {showFilters && (
                <div className="p-3 border-b border-ui-light-border dark:border-ui-dark-border bg-ui-light-hover/50 dark:bg-ui-dark-hover/50 flex-shrink-0">
                  <div className="space-y-3">
                    {/* Basic Search */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Basic Search</h3>
                        {activeSearchMethod === 'basic' && (
                          <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">Active</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                        <input
                          name="filter"
                          placeholder="Filename"
                          value={searchParams.filter}
                          onChange={handleSearchParamChange}
                          className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <input
                          name="objectName"
                          placeholder="Object name"
                          value={searchParams.objectName}
                          onChange={handleSearchParamChange}
                          className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <input
                          name="imageFilter"
                          placeholder="Filter (r, g, i, z)"
                          value={searchParams.imageFilter}
                          onChange={handleSearchParamChange}
                          className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          name="obsdateMin"
                          placeholder="Observation date min"
                          type="date"
                          value={searchParams.obsdateMin}
                          onChange={handleSearchParamChange}
                          className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <input
                          name="obsdateMax"
                          placeholder="Observation date max"
                          type="date"
                          value={searchParams.obsdateMax}
                          onChange={handleSearchParamChange}
                          className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    
                    {/* Point Search */}
                    <div>
                      <div 
                        className="flex items-center justify-between mb-2 cursor-pointer"
                        onClick={() => toggleSection('pointSearch')}
                      >
                        <h3 className="text-xs font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Point Search (Cone)</h3>
                        <div className="flex items-center gap-2">
                          {activeSearchMethod === 'point' && (
                            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">Active</span>
                          )}
                          <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transition-transform ${expandedSections.pointSearch ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      {expandedSections.pointSearch && (
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            name="raCentral"
                            placeholder="RA Center (deg)"
                            type="number"
                            step="0.0001"
                            value={searchParams.raCentral}
                            onChange={handleSearchParamChange}
                            className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                          <input
                            name="decCentral"
                            placeholder="Dec Center (deg)"
                            type="number"
                            step="0.0001"
                            value={searchParams.decCentral}
                            onChange={handleSearchParamChange}
                            className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                          <input
                            name="radius"
                            placeholder="Radius (deg)"
                            type="number"
                            step="0.01"
                            value={searchParams.radius}
                            onChange={handleSearchParamChange}
                            className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Region Search */}
                    
                    <div>
                      <div 
                        className="flex items-center justify-between mb-2 cursor-pointer"
                        onClick={() => toggleSection('regionSearch')}
                      >
                        <h3 className="text-xs font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Region Search (Box)</h3>
                        <div className="flex items-center gap-2">
                          {activeSearchMethod === 'region' && (
                            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">Active</span>
                          )}
                          <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transition-transform ${expandedSections.regionSearch ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        
                      </div>
                      {expandedSections.regionSearch && (
                      <div className="grid grid-cols-4 gap-2">
                        <input
                          name="raMin"
                          placeholder="RA Min"
                          type="number"
                          step="0.0001"
                          value={searchParams.raMin}
                          onChange={handleSearchParamChange}
                          className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <input
                          name="raMax"
                          placeholder="RA Max"
                          type="number"
                          step="0.0001"
                          value={searchParams.raMax}
                          onChange={handleSearchParamChange}
                          className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <input
                          name="decMin"
                          placeholder="Dec Min"
                          type="number"
                          step="0.0001"
                          value={searchParams.decMin}
                          onChange={handleSearchParamChange}
                          className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <input
                          name="decMax"
                          placeholder="Dec Max"
                          type="number"
                          step="0.0001"
                          value={searchParams.decMax}
                          onChange={handleSearchParamChange}
                          className="px-2 py-1 text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      </div>
                    )}
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex items-center justify-between pt-2">
                      {/* Lupa = Search */}
                      <button
                        onClick={handleSearch}
                        className="p-2 rounded border border-ui-light-border dark:border-ui-dark-border hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                        title="Search"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M12.9 14.32a8 8 0 111.414-1.414l3.387 3.386a1 1 0 01-1.414 1.415l-3.387-3.387zM14 8a6 6 0 11-12 0 6 6 0 0112 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>

                      {/* Clear All on the right */}
                      <button
                        onClick={clearFilters}
                        className="px-2 py-1 text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Image gallery with fixed pagination */}
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-auto">
                  {loading ? (
                    <div className="flex justify-center items-center h-32">
                      <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  ) : imageFiles.length === 0 ? (
                    <div className="flex flex-col justify-center items-center h-32 text-center p-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-ui-light-text-muted dark:text-ui-dark-text-muted mb-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        No images found. Try adjusting your search criteria.
                      </p>
                    </div>
                  ) : (
                    <div className="p-3">
                      <div className="space-y-2">
                        {imageFiles.map((image) => (
                          <div
                            key={image.id}
                            className="bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all hover:border-primary-300 dark:hover:border-primary-600"
                          >
                            <div className="flex items-center gap-3 p-3">
                              {/* Icon */}
                              <div className="flex-shrink-0 w-10 h-10 bg-ui-light-hover dark:bg-ui-dark-hover rounded flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-ui-light-text-muted dark:text-ui-dark-text-muted" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                </svg>
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-4 gap-2">
                                <div className="sm:col-span-2">
                                  <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary truncate" title={image.filename}>
                                    {image.filename}
                                  </h3>
                                  {image.object_name && (
                                    <p className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary truncate">
                                      {image.object_name}
                                    </p>
                                  )}
                                </div>

                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                                    {image.ra_center && image.dec_center
                                      ? `RA: ${image.ra_center.toFixed(4)}°, Dec: ${image.dec_center.toFixed(4)}°`
                                      : 'No coordinates'}
                                  </span>
                                </div>

                                <div className="flex items-center gap-2">
                                  {image.filter && (
                                    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                                      {image.filter}
                                    </span>
                                  )}
                                  {image.width && image.height && (
                                    <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                                      {image.width}×{image.height}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex-shrink-0 flex items-center gap-1">
                                <button
                                  className="p-1.5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                                  onClick={() => handleDownloadImage(image)}
                                  title="Download FITS file"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                <button
                                  className="p-1.5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                                  onClick={() => handleImageClick(image)}
                                  title="View details"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                {image.ra_center != null && image.dec_center != null && (
                                  <button
                                    className="p-1.5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                                    onClick={() => {
                                      openOverlay({
                                        collectionId: selectedCollection?.id,
                                        prefilledData: {
                                          ra_center: image.ra_center,
                                          dec_center: image.dec_center,
                                          filter: image.filter,
                                          object_name: image.object_name,
                                          file_id: image.id
                                        },
                                        activeTab: 'stamp'
                                      });
                                    }}
                                    title="Open in Image Tools"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}
                                <button
                                  className="p-1.5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 rounded hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                                  onClick={() => setCodePopup({
                                    isOpen: true,
                                    snippetName: 'image_download',
                                    dynamicData: {
                                      base_url: window.location.origin,
                                      file_id: image.id,
                                      ra: image.ra_center?.toFixed(6) || '0.0',
                                      dec: image.dec_center?.toFixed(6) || '0.0'
                                    }
                                  })}
                                  title="Show Python code"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Fixed Pagination - always at bottom */}
                {totalPages > 1 && (
                  <div className="p-2 border-t border-ui-light-border dark:border-ui-dark-border bg-ui-light-hover dark:bg-ui-dark-hover flex-shrink-0">
                    <div className="flex justify-center">
                      <div className="inline-flex rounded-md shadow-sm">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={page === 1}
                          className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          First
                        </button>
                        <button
                          onClick={() => handlePageChange(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="relative inline-flex items-center px-2 py-1 border-t border-b border-l border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Prev
                        </button>
                        
                        {/* Current page indicator */}
                        <span className="relative inline-flex items-center px-3 py-1 border border-primary-500 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30 text-xs font-medium text-primary-600 dark:text-primary-400">
                          {page} of {totalPages}
                        </span>
                        
                        <button
                          onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                          disabled={page === totalPages}
                          className="relative inline-flex items-center px-2 py-1 border-t border-b border-r border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          disabled={page === totalPages}
                          className="relative inline-flex items-center px-2 py-1 rounded-r-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-ui-light-hover dark:bg-ui-dark-hover rounded-lg shadow-md flex flex-col justify-center items-center text-center p-8" style={{ height: 'calc(100vh - 200px)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-ui-light-text-muted dark:text-ui-dark-text-muted mb-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
              </svg>
              <h2 className="text-lg font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-2">
                Select a collection to browse images
              </h2>
              {collections.length === 0 && (
                <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  No collections available. You may need to request access.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Compact Image Detail Dialog */}
      {openImageDialog && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-ui-light-border dark:border-ui-dark-border">
              <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary truncate">
                {selectedImage.filename}
              </h2>
              <button
                onClick={() => setOpenImageDialog(false)}
                className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-ui-light-text-primary dark:hover:text-ui-dark-text-primary transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 overflow-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="h-48 bg-ui-light-hover dark:bg-ui-dark-hover rounded-md flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-ui-light-text-muted dark:text-ui-dark-text-muted" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-base font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-3">Image Metadata</h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">RA Center:</div>
                      <div className="col-span-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {selectedImage.ra_center ? `${selectedImage.ra_center.toFixed(5)}°` : 'Not available'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Dec Center:</div>
                      <div className="col-span-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {selectedImage.dec_center ? `${selectedImage.dec_center.toFixed(5)}°` : 'Not available'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Object:</div>
                      <div className="col-span-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {selectedImage.object_name || 'Not available'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Dimensions:</div>
                      <div className="col-span-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {selectedImage.width && selectedImage.height ? `${selectedImage.width} × ${selectedImage.height} px` : 'Not available'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Pixel Scale:</div>
                      <div className="col-span-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {selectedImage.pixel_scale ? `${selectedImage.pixel_scale.toFixed(3)} arcsec/px` : 'Not available'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Filter:</div>
                      <div className="col-span-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {selectedImage.filter || 'Not available'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Telescope:</div>
                      <div className="col-span-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {selectedImage.telescope || 'Not available'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Instrument:</div>
                      <div className="col-span-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {selectedImage.instrument || 'Not available'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Observation:</div>
                      <div className="col-span-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        {selectedImage.date_obs ? new Date(selectedImage.date_obs).toLocaleDateString() : 'Not available'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 p-3 border-t border-ui-light-border dark:border-ui-dark-border">
              <button
                onClick={() => handleDownloadImage(selectedImage)}
                className="px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 rounded-md hover:bg-primary-200 dark:hover:bg-primary-800/50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors inline-flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Download
              </button>
              <button
                onClick={() => {
                  openOverlay({
                    collectionId: selectedCollection?.id,
                    prefilledData: {
                      ra_center: selectedImage.ra_center,
                      dec_center: selectedImage.dec_center,
                      filter: selectedImage.filter,
                      object_name: selectedImage.object_name
                    },
                    activeTab: 'stamp'
                  });
                  setOpenImageDialog(false);
                }}
                className="px-3 py-1.5 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-hover dark:bg-ui-dark-hover rounded-md hover:bg-ui-light-card dark:hover:bg-ui-dark-card focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors inline-flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h8V3a1 1 0 112 0v1h1a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2h1V3a1 1 0 011-1zm2 3a1 1 0 011 1v1h4V6a1 1 0 112 0v1h1v3H5V7h1V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Open in Image Tools
              </button>
              <button
                onClick={() => setOpenImageDialog(false)}
                className="px-3 py-1.5 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Code Popup */}
      <CodePopup
        isOpen={codePopup.isOpen}
        onClose={() => setCodePopup({ isOpen: false, snippetName: '', dynamicData: {} })}
        snippetName={codePopup.snippetName}
        dynamicData={codePopup.dynamicData}
      />
    </div>
  );
};

export default ImageBrowser;